import { useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";
import { getStatusLabel } from "~/pages/purchase-orders/utils/statusLabels";

export type StatusHistoryRecord = {
  id: string;
  sales_request_id: string;
  old_status: string | null;
  new_status: string;
  changed_by: string | null;
  changed_at: string;
  comments?: string | null;
  display_name?: string;
  color?: string;
  description?: string;
};

export const useStatusHistory = (salesRequestId: string | null) => {
  const [history, setHistory] = useState<StatusHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStatusHistory = async () => {
      if (!salesRequestId) {
        setHistory([]);
        return;
      }
      setLoading(true);

      try {
        // First, get the status history
        const { data, error } = await supabase
          .from("sales_request_status_history")
          .select(
            `id, sales_request_id, old_status, new_status, changed_by, changed_at, comments`
          )
          .eq("sales_request_id", salesRequestId)
          .order("changed_at", { ascending: true });

        if (error) throw error;

        if (!data || data.length === 0) {
          setHistory([]);
          setLoading(false);
          return;
        }

        // Then fetch status translations separately
        const statuses = Array.from(
          new Set(data.map((item) => item.new_status))
        );
        const { data: translations, error: translationsError } = await supabase
          .from("status_translations")
          .select("status, display_name, color, description")
          .in("status", statuses);

        if (translationsError) throw translationsError;

        // Create a lookup map for translations
        const translationsMap = (translations || []).reduce(
          (acc: Record<string, any>, item) => {
            acc[item.status] = item;
            return acc;
          },
          {}
        );

        // Combine the data
        const enrichedHistory = data.map((item) => ({
          ...item,
          display_name:
            translationsMap[item.new_status]?.display_name ||
            getStatusLabel(item.new_status),
          color: translationsMap[item.new_status]?.color,
          description: translationsMap[item.new_status]?.description,
        }));

        setHistory(enrichedHistory);
      } catch (error) {
        console.error("Error fetching status history:", error);
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStatusHistory();
  }, [salesRequestId]);

  return { history, loading };
};
